// Mantis #1453 — M001-D029 §5, contract SAPI-D029-004
// (interfaces/core-realmauthority-editor/v1). Sanitising projection for the
// Access Authority editor. Closed shapes: unknown fields are dropped, never
// echoed raw; bad enum values fall to an explicit unrecognised/null state
// (the AccessControlPanel CARD_STATE_ALIASES discipline) — a malformed
// payload is NEVER partially rendered as if it were trustworthy. Names are
// never a correlation signal (decision R): the person key everywhere below
// is objectSid only.
//
// CARDINALITY IS 1:1 (lead ruling, Mantis #1453): one RealmAuthority realm
// per organisation. There is no installation id anywhere in this module —
// the #1378 connectInstallationId scoping is retired along with the
// multi-installation UI and fixtures it supported. `linked:false` is a
// normal, explicit, renderable state this projection never infers from
// absence — it comes only from the contract's own `linked` field.

export const SID_PATTERN = /^S-1-[0-9]+(-[0-9]+)+$/;
const UUIDISH = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const GRANT_KINDS = new Set(['door', 'radius']);
const MEMBERSHIP_OPS = new Set(['add', 'remove']);
const RETAINED_REASONS = new Set(['OTHER_GROUP_GRANT', 'LEGACY_CARD_RECORD', 'BREAK_GLASS_ALLOWLIST', 'REMOVAL_WITHHELD']);
const REFUSAL_REASONS = new Set(['NOT_OPTED_IN', 'NOT_IN_DIRECTORY', 'NOT_EXPORTABLE']);
const DOOR_WRITE_STATUSES = new Set(['OK', 'DOOR_WRITE_BLOCKED']);
// Mantis #1476 owner directive 2026-09-13: these two DANCEADDICTION.LOCAL
// people are never offerable in the add-person picker — dropped client-side
// when the directory view is built (exact UPN, case-insensitive).
const EXCLUDED_UPNS = new Set(['svetlana@danceaddiction.local', 'aisling@danceaddiction.local']);

const text = (value, max) => {
  if (typeof value !== 'string') return '';
  const clean = value.replace(/[\x00-\x1f\x7f]/g, '').trim();
  if (!max || clean.length <= max) return clean;
  return `${clean.slice(0, max - 1)}…`;
};

const iso = (value, max = 40) => {
  const clean = text(value, max);
  return clean && !Number.isNaN(Date.parse(clean)) ? clean : '';
};

// Nullable ISO date-time: distinguishes "explicitly null" from "not a valid
// date" per the contract's honest-unknown discipline — a caller that needs
// to tell "never published" (null) from "unparseable" ('') can still do so.
const isoOrNull = (value) => (value === null ? null : (iso(value) || null));

const sid = (value) => {
  const clean = text(value, 128);
  return SID_PATTERN.test(clean) ? clean : '';
};

const uuidOrNull = (value) => {
  const clean = text(value, 64);
  return UUIDISH.test(clean) ? clean : null;
};

const op = (value) => {
  const clean = text(value, 8);
  return MEMBERSHIP_OPS.has(clean) ? clean : null;
};

const normaliseGrant = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const kind = text(value.kind, 32);
  const label = text(value.label, 200);
  if (!GRANT_KINDS.has(kind)) return { kind: 'unrecognised', label };
  if (!label) return null;
  return { kind, label };
};

// Null when RA published no grants block at all (distinct from an empty
// array, RA published a grants block with zero grants) — never collapsed.
const normaliseGrants = (value) => {
  if (value === null) return null;
  if (!Array.isArray(value)) return null;
  return value.map(normaliseGrant).filter((grant) => grant && !(grant.kind === 'unrecognised' && !grant.label));
};

const normaliseGroupMember = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const objectSid = sid(value.objectSid ?? value.object_sid);
  const upn = text(value.upn, 320);
  if (!objectSid || !upn) return null;
  const rawDisplayName = value.displayName ?? value.display_name;
  return {
    objectSid,
    upn,
    // Null when this member's SID has no current directory hit — the group
    // snapshot is authoritative for membership, never dropped for lacking one.
    displayName: rawDisplayName === null ? null : (text(rawDisplayName, 256) || null),
    alsoCoreAccount: value.alsoCoreAccount === true,
  };
};

const normalisePendingCommand = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const commandId = uuidOrNull(value.commandId ?? value.command_id);
  const objectSid = sid(value.objectSid ?? value.object_sid);
  const commandOp = op(value.op);
  if (!commandId || !objectSid || !commandOp) return null;
  return {
    commandId,
    objectSid,
    op: commandOp,
    dispatchedAt: iso(value.dispatchedAt ?? value.dispatched_at),
  };
};

const normaliseGroup = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const groupSid = sid(value.groupSid ?? value.group_sid);
  const groupName = text(value.groupName ?? value.group_name, 256);
  if (!groupSid || !groupName) return null;
  const members = (Array.isArray(value.members) ? value.members : [])
    .map(normaliseGroupMember)
    .filter(Boolean);
  const pending = (Array.isArray(value.pending) ? value.pending : [])
    .map(normalisePendingCommand)
    .filter(Boolean);
  const snapshotRevision = Number.isSafeInteger(value.snapshotRevision)
    ? value.snapshotRevision : 0;
  return {
    groupSid,
    groupName,
    description: text(value.description, 4000),
    grants: normaliseGrants(value.grants),
    members,
    snapshotRevision,
    publishedAt: iso(value.publishedAt ?? value.published_at),
    mirroredAt: iso(value.mirroredAt ?? value.mirrored_at),
    pending,
  };
};

// getRealmAuthorityLinkStatus → sanitised link status, or null when the
// payload cannot be verified at all (an explicit error state, never a guess).
export const projectLinkStatus = (payload) => {
  const data = payload?.data?.data ?? payload?.data ?? payload;
  if (!data || typeof data !== 'object' || Array.isArray(data) || typeof data.linked !== 'boolean') return null;
  return {
    linked: data.linked,
    installationUuid: uuidOrNull(data.installationUuid ?? data.installation_uuid),
    tunnelConnected: data.tunnelConnected === true,
    lastSnapshotAt: isoOrNull(data.lastSnapshotAt ?? data.last_snapshot_at),
  };
};

// getRealmAuthorityGroups → sanitised groups view, or null when unverifiable.
export const projectGroupsView = (payload) => {
  const data = payload?.data?.data ?? payload?.data ?? payload;
  if (!data || typeof data !== 'object' || Array.isArray(data) || typeof data.linked !== 'boolean') return null;
  const groups = (Array.isArray(data.groups) ? data.groups : [])
    .map(normaliseGroup)
    .filter(Boolean);
  return {
    linked: data.linked,
    tunnelConnected: data.tunnelConnected === true,
    lastSnapshotAt: isoOrNull(data.lastSnapshotAt ?? data.last_snapshot_at),
    groups,
  };
};

const normaliseDirectoryMember = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const objectSid = sid(value.objectSid ?? value.object_sid);
  const displayName = text(value.displayName ?? value.display_name, 256);
  const upn = text(value.upn, 320);
  if (!objectSid || !upn) return null;
  return {
    objectSid,
    samAccountName: text(value.samAccountName ?? value.sam_account_name, 256),
    displayName,
    upn,
    // Informational only (decision R): computed Core-side, never eligibility.
    alsoCoreAccount: value.alsoCoreAccount === true,
  };
};

// getRealmAuthorityDirectory → sanitised directory view, or null when
// unverifiable. This is the ONLY eligible picker source (AMENDMENT 2 §R).
export const projectDirectoryView = (payload) => {
  const data = payload?.data?.data ?? payload?.data ?? payload;
  if (!data || typeof data !== 'object' || Array.isArray(data) || typeof data.linked !== 'boolean') return null;
  const snapshot = data.snapshot && typeof data.snapshot === 'object' && !Array.isArray(data.snapshot)
    ? data.snapshot : {};
  const members = (Array.isArray(snapshot.members) ? snapshot.members : [])
    .map(normaliseDirectoryMember)
    .filter(Boolean)
    // Mantis #1476 owner directive 2026-09-13: the two excluded people are
    // dropped HERE, when the picker source is built — never offered, however
    // they arrive from the published directory.
    .filter((member) => !EXCLUDED_UPNS.has(member.upn.trim().toLowerCase()));
  return {
    linked: data.linked,
    snapshot: {
      members,
      publishedAt: isoOrNull(snapshot.publishedAt ?? snapshot.published_at),
    },
  };
};

// Directory entries keyed by objectSid — for joining the group snapshot's
// {objectSid, upn} member rows to display-only names.
export const directoryBySid = (members) => {
  const map = new Map();
  for (const entry of members || []) map.set(entry.objectSid, entry);
  return map;
};

// Join one group's mirror members to the current directory. A member whose
// objectSid is absent from the current directory keeps its mirror row with
// inPublishedDirectory: false — the editor renders it as "no longer in the
// published directory" and disables its toggle (a command would be refused
// NOT_IN_DIRECTORY; say why the control is dead rather than let it fail).
export const enrichMembers = (group, directoryMap) => (group?.members || []).map((member) => {
  const entry = directoryMap?.get(member.objectSid);
  return {
    objectSid: member.objectSid,
    upn: member.upn,
    displayName: member.displayName || entry?.displayName || '',
    samAccountName: entry?.samAccountName || '',
    alsoCoreAccount: member.alsoCoreAccount || entry?.alsoCoreAccount === true,
    inPublishedDirectory: Boolean(entry),
  };
});

// One MembershipChangeOutcome, discriminated by outcome (contract:
// applied|refused|failed — exactly one of the three closed shapes).
// checkOutcome returns {outcome} for a conformant entry or {fault} naming the
// FIRST rule it broke (field name only — never a value; M004-F0671). The
// projection and its diagnostic share this one function, so the diagnostic
// can never drift from the validation it explains. A non-conformant entry
// invalidates the WHOLE response projection (projectMembershipBatch below),
// never a silently-dropped row.
const checkOutcome = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return { fault: '' };
  const objectSid = sid(value.objectSid);
  if (!objectSid) return { fault: '.objectSid' };
  const changeOp = op(value.op);
  if (!changeOp) return { fault: '.op' };
  const outcome = text(value.outcome, 16);
  const message = text(value.message, 1000);
  if (!message) return { fault: '.message' };

  if (outcome === 'applied') {
    const commandId = uuidOrNull(value.commandId);
    if (!commandId) return { fault: '.commandId' };
    // 1.1.0 (Core #164): doorWriteStatus is REQUIRED-PRESENT on an applied
    // outcome with an explicit NULL branch — null means the door consequence
    // has NOT YET SETTLED (an applied ack never waits on RA's door-write
    // engine; the settled status arrives later on group-sync's
    // realmauthority.command.door-write-outcome event). There is deliberately
    // NO PENDING value. Absent, or a non-null value outside the closed
    // {OK, DOOR_WRITE_BLOCKED} set, still refuses the row.
    const doorWriteStatus = value.doorWriteStatus === null
      ? null
      : text(value.doorWriteStatus, 32);
    if (!(doorWriteStatus === null || DOOR_WRITE_STATUSES.has(doorWriteStatus))) return { fault: '.doorWriteStatus' };
    // Ruling 3 (Mantis #1453): DOOR_WRITE_BLOCKED is a CONSEQUENCE of a
    // SUCCESSFUL change, never a refusal — outcome stays 'applied'
    // unconditionally; doorWriteStatus is carried alongside it, never
    // collapsed into a failure.
    return {
      outcome: { objectSid, op: changeOp, outcome: 'applied', commandId, doorWriteStatus, message },
    };
  }
  if (outcome === 'refused') {
    const commandId = uuidOrNull(value.commandId); // null is valid: refused pre-dispatch
    const refusalReason = text(value.refusalReason, 32);
    if (!REFUSAL_REASONS.has(refusalReason)) return { fault: '.refusalReason' };
    return {
      outcome: { objectSid, op: changeOp, outcome: 'refused', commandId, refusalReason, message },
    };
  }
  if (outcome === 'failed') {
    const commandId = uuidOrNull(value.commandId);
    const failureReason = text(value.failureReason, 128);
    // open string (EDITOR-GAP-PROTOCOL-FAILURE-TAXONOMY), just non-empty
    if (!failureReason) return { fault: '.failureReason' };
    return {
      outcome: { objectSid, op: changeOp, outcome: 'failed', commandId, failureReason, message },
    };
  }
  return { fault: '.outcome' };
};

const normaliseOutcome = (value) => checkOutcome(value).outcome || null;

const batchData = (payload) => payload?.data?.data ?? payload?.data ?? payload;

// submitGroupMembershipChanges 200 response → {results, lastSnapshotAt}, or
// null when the batch cannot be fully verified. Every submitted change must
// be accounted for by a conformant provider; if any entry does not parse as
// one of the three closed outcome shapes, the WHOLE batch is treated as
// unverifiable rather than rendering some rows as trustworthy and silently
// dropping others.
export const projectMembershipBatch = (payload) => {
  const data = batchData(payload);
  if (!data || typeof data !== 'object' || Array.isArray(data) || !Array.isArray(data.results) || !data.results.length) {
    return null;
  }
  const results = data.results.map(normaliseOutcome);
  if (results.some((entry) => entry === null)) return null;
  return {
    results,
    lastSnapshotAt: isoOrNull(data.lastSnapshotAt ?? data.last_snapshot_at),
  };
};

// M004-F0698/F0699 (editor contract 1.2.0, RA #1579): one entry of
// getRealmAuthorityMembershipDoorOutcomes. Closed shape; an entry that does
// not conform is DROPPED (it can then never upgrade a row), never partially
// trusted. retainedAccess is all-or-nothing for the same reason: dropping a
// bad entry from the list would understate who keeps door access, so one bad
// entry invalidates the whole outcome. Only door labels, group names and the
// closed reason are kept — nothing from the wire that could be card
// material (stationEmployeeNo / legacyEmployeeNo are deliberately not
// carried into the UI model).
const normaliseRetainedEntry = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const doorLabel = text(value.doorLabel, 256);
  if (!doorLabel) return null;
  const reason = text(value.reason, 32);
  if (!RETAINED_REASONS.has(reason)) return null;
  if (!Array.isArray(value.viaGroups)) return null;
  const viaGroups = [];
  for (const group of value.viaGroups) {
    if (!group || typeof group !== 'object' || Array.isArray(group)) return null;
    const name = text(group.name, 256);
    if (!name) return null;
    viaGroups.push({ name });
  }
  return { doorLabel, reason, viaGroups };
};

const normaliseDoorOutcome = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const commandId = uuidOrNull(value.commandId);
  const objectSid = sid(value.objectSid);
  const changeOp = op(value.op);
  if (!commandId || !objectSid || !changeOp) return null;
  const status = value.doorWriteStatus === null ? null : text(value.doorWriteStatus, 32);
  if (!(status === null || DOOR_WRITE_STATUSES.has(status))) return null;
  let retainedAccess = null;
  if (value.retainedAccess !== null) {
    if (!Array.isArray(value.retainedAccess)) return null;
    retainedAccess = [];
    for (const entry of value.retainedAccess) {
      const parsed = normaliseRetainedEntry(entry);
      if (!parsed) return null;
      retainedAccess.push(parsed);
    }
  }
  const detail = value.doorWriteDetail === null ? null : (text(value.doorWriteDetail, 1000) || null);
  return {
    commandId, objectSid, op: changeOp, doorWriteStatus: status, doorWriteAt: isoOrNull(value.doorWriteAt), doorWriteDetail: detail, retainedAccess,
  };
};

// getRealmAuthorityMembershipDoorOutcomes 200 → {groupSid, outcomes} or null
// when the body is not the contract's shape or answers for another group.
export const projectMembershipDoorOutcomes = (payload, expectedGroupSid) => {
  const data = batchData(payload);
  if (!data || typeof data !== 'object' || Array.isArray(data) || !Array.isArray(data.outcomes)) return null;
  const groupSid = sid(data.groupSid);
  if (!groupSid || (expectedGroupSid && groupSid !== expectedGroupSid)) return null;
  return { groupSid, outcomes: data.outcomes.map(normaliseDoorOutcome).filter(Boolean) };
};

// The ONLY correlation rule: the door outcome belongs to an acked row when
// the commandId is EQUAL (and the person and op agree). Anything else is not
// this row's result.
export const matchDoorOutcome = (ackOutcome, doorOutcomes) => (
  (doorOutcomes || []).find((entry) => (
    ackOutcome?.commandId && entry.commandId === ackOutcome.commandId
    && entry.objectSid === ackOutcome.objectSid && entry.op === ackOutcome.op
  )) || null
);

// M004-F0671: WHY a submitGroupMembershipChanges answer did not project —
// the first rule projectMembershipBatch rejected, as a path/field name only
// ("body", "body.results", "results[1].doorWriteStatus"). Null when the batch
// projects. Never carries a value from the payload (no SID, UUID, message or
// received enum value — the payload is untrusted wire data).
export const explainMembershipBatch = (payload) => {
  const data = batchData(payload);
  if (!data || typeof data !== 'object' || Array.isArray(data)) return 'body';
  if (!Array.isArray(data.results) || !data.results.length) return 'body.results';
  for (let index = 0; index < data.results.length; index += 1) {
    const { fault } = checkOutcome(data.results[index]);
    if (fault !== undefined) return `results[${index}]${fault}`;
  }
  return null;
};

// M004-F0671: the value-free context for the diagnostic line — the JS type
// of the decoded body (axios response.data), the response media type reduced
// to type/subtype, and the running bundle's build id (M004-F0651's
// data-build-id). Each piece is normalised to a closed character set.
const MEDIA_TYPE = /^[a-z0-9.+-]{1,32}\/[a-z0-9.+-]{1,32}$/;
export const describeUnverifiedResponse = (response, buildId) => {
  const fault = explainMembershipBatch(response) || 'none';
  const body = response && typeof response === 'object' && 'data' in response
    ? (Array.isArray(response.data) ? 'array' : (response.data === null ? 'null' : typeof response.data))
    : 'no-response';
  const headers = response?.headers;
  const rawType = typeof headers?.get === 'function' ? headers.get('content-type') : headers?.['content-type'];
  const mediaType = typeof rawType === 'string' ? rawType.split(';')[0].trim().toLowerCase() : '';
  const build = typeof buildId === 'string' && /^[A-Za-z0-9._-]{1,64}$/.test(buildId) ? buildId : 'unknown';
  return `${fault}; body ${body}; ${MEDIA_TYPE.test(mediaType) ? mediaType : 'none'}; build ${build}`;
};

// M004-F0628 (draft retention): the per-change results a whole-request 409
// may carry once Core reports partial progress — RealmAuthority went
// offline PART-WAY through a batch, so the entries processed before that
// point already have a resolved outcome. Assumed shape (stated in the MR;
// Core's own lane is landing it concurrently): the 409 error body carries
// `results` either at the top level or under `details`, each entry a
// MembershipChangeOutcome exactly as the 200 response uses. Returns:
// - [] when the body carries no results at all (today's 409 shape) — the
//   editor keeps the WHOLE draft;
// - null when a results array is present but any entry is malformed — the
//   projection is untrustworthy, so the editor again keeps the whole draft
//   rather than clearing an entry on an unverified claim;
// - the parsed outcomes otherwise.
export const projectPartialResults = (payload) => {
  const data = payload?.data?.data ?? payload?.data ?? payload;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return [];
  const raw = Array.isArray(data.results) ? data.results
    : (Array.isArray(data.details?.results) ? data.details.results : null);
  if (!raw) return [];
  const results = raw.map(normaliseOutcome);
  if (results.some((entry) => entry === null)) return null;
  return results;
};

// MAD001-F0236: one person-rename command outcome (the drawer's rename
// commit response), discriminated by outcome — applied|refused|failed, the
// same closed three-shape discipline as the membership batch above. Returns
// null on anything that does not conform: an unverifiable response is an
// explicit error state in the drawer (nothing is shown as applied; the
// drawer stays editable), never a guessed success.
//   applied  commandId REQUIRED, person snapshot REQUIRED (objectSid +
//            samAccountName + displayName + upn — the ack's post-rename
//            state, already applied to Core's directory mirror), superseded
//            informational (the app's own publish had already delivered the
//            same name).
//   refused  commandId nullable (refused pre-dispatch), refusalReason
//            surfaced verbatim, message REQUIRED.
//   failed   failureReason non-empty (open string, the membership
//            convention), message REQUIRED — includes Core's tunnel-SLA
//            timeout (failureReason RA_OFFLINE).
const normaliseRenamedPerson = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const objectSid = sid(value.objectSid);
  const samAccountName = text(value.samAccountName ?? value.sam_account_name, 256);
  const displayName = text(value.displayName ?? value.display_name, 256);
  const upn = text(value.upn, 320);
  if (!objectSid || !upn || !displayName) return null;
  return { objectSid, samAccountName, displayName, upn };
};

export const projectPersonRenameResult = (payload) => {
  const data = payload?.data?.data ?? payload?.data ?? payload;
  const result = data?.result;
  if (!result || typeof result !== 'object' || Array.isArray(result)) return null;
  const objectSid = sid(result.objectSid);
  const outcome = text(result.outcome, 16);
  const message = text(result.message, 1000);
  if (!objectSid || !message) return null;

  if (outcome === 'applied') {
    const commandId = uuidOrNull(result.commandId);
    const person = normaliseRenamedPerson(result.person);
    if (!commandId || !person) return null;
    return { objectSid, outcome: 'applied', commandId, person, superseded: result.superseded === true, message };
  }
  if (outcome === 'refused') {
    const commandId = uuidOrNull(result.commandId); // null is valid: refused pre-dispatch
    const refusalReason = text(result.refusalReason, 64);
    if (!refusalReason) return null;
    return { objectSid, outcome: 'refused', commandId, refusalReason, message };
  }
  if (outcome === 'failed') {
    const commandId = uuidOrNull(result.commandId);
    const failureReason = text(result.failureReason, 128);
    if (!failureReason) return null;
    return { objectSid, outcome: 'failed', commandId, failureReason, message };
  }
  return null;
};

// M004-F0628: which staged entries a set of outcomes RESOLVES. Only
//    'applied' and 'refused' are answers — RealmAuthority took the command
// and either did it or said no. A 'failed' outcome (a malformed ack, a
// missing snapshot, or Core's ack wait expiring — failureReason RA_OFFLINE)
// is NOT an answer: the change may or may not have happened, so its entry
// stays staged and is reconciled against the real mirror on the next
// re-fetch (pruneDraft below). Entries with no outcome at all were never
// processed and stay staged for a retry without re-entry.
export const resolvedSids = (outcomes) => new Set(
  (outcomes || [])
    .filter((entry) => entry.outcome === 'applied' || entry.outcome === 'refused')
    .map((entry) => entry.objectSid),
);

// Reconcile a staged draft against the freshly re-fetched mirror: a staged
// add whose person is now a member is done; a staged removal whose person
// is no longer a member is done. Anything else is still the operator's
// intent and is kept.
export const pruneDraft = ({ stagedAdds, stagedRemovals }, mirrorSids) => ({
  stagedAdds: (stagedAdds || []).filter((objectSid) => !mirrorSids.has(objectSid)),
  stagedRemovals: (stagedRemovals || []).filter((objectSid) => mirrorSids.has(objectSid)),
});

// M001-D029 §6.1 / SAPI-D029-004: Core's ack wait for a dispatched command
// is short (3 s) and deliberately shorter than RealmAuthority's own AD work
// on a slow domain controller, so a 'failed' outcome with failureReason
// RA_OFFLINE is common AND the change frequently did apply. The editor
// says so instead of echoing "went offline before this command could
// resolve" verbatim (M004-F0630). The timeout itself is Core's and is
// never changed here.
export const TIMED_OUT_FAILURE_REASON = 'RA_OFFLINE';
export const isTimedOutOutcome = (outcome) => (
  outcome?.outcome === 'failed' && outcome?.failureReason === TIMED_OUT_FAILURE_REASON
);

// Display-only: an ISO date-time rendered in the viewer's locale and zone.
// Falls back to the verified ISO text when the runtime cannot format it —
// never to an empty string, which would hide a real timestamp.
export const formatDateTime = (value) => {
  const clean = iso(value);
  if (!clean) return '';
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(clean));
  } catch {
    return clean;
  }
};

// T-M001-RASYNC-013 render-side half: the page renders grant labels only —
// never a SID, card code, NAS identifier or share path from the publish
// payload. Unknown kinds are labelled unrecognised rather than echoed.
export const grantLabel = (grant) => {
  if (!grant) return '';
  if (grant.kind === 'unrecognised') return 'Unrecognised grant type';
  return grant.label;
};
